import React from 'react';
import { useSelector } from 'react-redux';
import {
    selectDataIsLoading,
    selectDataIsUpdating,
    selectKeyboardShortcuts,
    setEditedShortcut,
    setDeletedShortcut,
    getShortcutDescription,
} from 'modules/settings/stores/user-settings-slice-handler';
import store from 'modules/shared/stores/store';
import Preloader from 'modules/shared/components/preloader';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import KeyboardShortcutEditor from './keyboard-shortcut-editor';
import t from 'utils/translate';

function SectionKeyboardShortcuts() {
    const dataIsLoading = useSelector(selectDataIsLoading);
    const dataIsUpdating = useSelector(selectDataIsUpdating);

    /**
     * @type {KeyboardShortcut[]}
     */
    const shortcuts = useSelector(selectKeyboardShortcuts);

    const execShortcutAction = (shortcut, action) => {
        if (action === 'edit') {
            store.dispatch(setEditedShortcut(shortcut));
        }

        if (action === 'delete' && shortcut.enabled) {
            store.dispatch(setDeletedShortcut(shortcut));
        }
    };

    /**
     * Render a shortcut.
     * @param {KeyboardShortcut} shortcut
     * @return {JSX.Element}
     */
    const renderShortcut = (shortcut) => {
        const lcType = shortcut.type.toLowerCase();
        const title = t(`keyboard_shortcut_${lcType}_title`);
        const info = t(`keyboard_shortcut_${lcType}_info`);

        /**
         * This is no longer configurable by user
         * https://jira.streamwide.com/browse/SMARTMS-50693
         */
        if (shortcut.type === 'FLOOR_CONTROL') {
            return '';
        }

        return (
            <div className="section-row" data-test-id={`shortcut-${lcType}`} key={lcType}>
                <LabelWithInfo text={title} info={info} tooltipPlacement="right" />
                <div className="shortcut-description">{getShortcutDescription(shortcut)}</div>
                <div className="shortcut-controls">
                    <span className="stw-edit-pen" onClick={() => execShortcutAction(shortcut, 'edit')} />
                    <span
                        className={`${shortcut.enabled ? '' : 'disabled'} stw-delete-bin`}
                        onClick={() => execShortcutAction(shortcut, 'delete')}
                    />
                </div>
            </div>
        );
    };

    let content;

    if (dataIsLoading) {
        content = <Preloader />;
    } else {
        content = shortcuts.map((keyboardShortcut) => renderShortcut(keyboardShortcut));
    }

    return (
        <div className="settings-section keyboard-shortcuts" data-test-id="keyboard-shortcuts">
            <div className="settings-section-title">
                <LabelWithInfo
                    text={t('keyboard_shortcuts_title')}
                    info={t('keyboard_shortcuts_info')}
                    tooltipPlacement="right"
                />
                <div className="strike" />
            </div>
            <div className={'section-content'}>{content}</div>
            <KeyboardShortcutEditor />
            {dataIsUpdating && <Preloader asOverlay />}
        </div>
    );
}
export default SectionKeyboardShortcuts;
